"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Textarea = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const styled_1 = __importDefault(require("@emotion/styled"));
const react_1 = require("react");
const Label_1 = require("./Label");
const constants_1 = require("../../constants");
const useValidate_1 = require("../../hooks/useValidate");
const utils_1 = require("../../utils");
const ErrorMessage_1 = require("../ErrorMessage");
const TextareaElement = styled_1.default.textarea((props) => ({
    background: constants_1.Color.LIGHT_GREY,
    border: constants_1.theme.border.style.thinMedium,
    ...(props.invalid && { borderColor: constants_1.Color.RED }),
    borderRadius: constants_1.theme.border.radius.normal,
    boxSizing: 'border-box',
    // Ensure that we don't use the browser's default font:
    fontFamily: 'inherit',
    lineHeight: 1.4,
    ...(0, constants_1.inputFontSize)({}),
    minHeight: props.minHeight ?? 128,
    maxHeight: '50vh',
    padding: `${constants_1.theme.spacing['2']}px ${constants_1.theme.spacing['2.5']}px`,
    resize: 'none',
    width: props.fullWidth ? '100%' : undefined,
    '&:disabled': {
        background: constants_1.Color.DARK_MEDIUM_GREY,
    },
    ...(0, utils_1.createStyles)(props.styles),
}));
const TextareaLabel = (0, styled_1.default)(Label_1.Label)({
    width: '100%',
});
function resize(element) {
    /*
      First we resize the element to 0px height to make it overflow, then we can use scrollHeight to
      figure out how big it should be to not have a scrollbar.
  
      + 1 is used so that we won't have sub-pixel rounding issues. Showing scrollbar when there
      shouldn't be one.
    */
    element.style.height = '0px';
    element.style.height =
        element.scrollHeight + 2 * constants_1.theme.border.width.thin + 1 + 'px';
}
const Textarea = (props) => {
    const [touched, setTouched] = (0, react_1.useState)(false);
    const validateError = (0, useValidate_1.useValidate)(props.schema, props.required, props.value);
    const ref = (0, react_1.useRef)(null);
    (0, react_1.useEffect)(() => {
        const element = ref.current;
        if (!element || !props.autogrow) {
            return;
        }
        // If using a really old browser, we just don't resize the input on e.g. window resize
        if (!window.ResizeObserver) {
            return;
        }
        const resizeObserver = new ResizeObserver(() => {
            resize(element);
        });
        resizeObserver.observe(element);
        return () => {
            resizeObserver.unobserve(element);
        };
    }, []);
    (0, react_1.useEffect)(() => {
        if (props.autogrow && ref.current) {
            resize(ref.current);
        }
    }, [props.value, ref.current]);
    const errorMessage = props.errorMessage ?? validateError;
    const showError = (touched || props.forceShowError) && !!errorMessage;
    const elementAndError = ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)(TextareaElement, { autoFocus: props.autoFocus, disabled: props.disabled, fullWidth: props.fullWidth, minHeight: props.minHeight, invalid: showError, onBlur: () => {
                    setTouched(true);
                    props.onBlur?.();
                }, onChange: (event) => {
                    props.onChange?.(event.target.value);
                }, onFocus: props.onFocus, onKeyDown: props.onKeyDown, placeholder: props.placeholder || (props.label ? `${props.label}...` : undefined), ref: ref, required: props.required, styles: props.styles, value: props.value ?? undefined }), (0, jsx_runtime_1.jsx)(ErrorMessage_1.ErrorMessage, { errorMessage: showError ? errorMessage : undefined, reserveSpace: props.reserveErrorSpace })] }));
    return props.label ? ((0, jsx_runtime_1.jsx)(TextareaLabel, { description: props.description, fullWidth: props.fullWidth, required: props.required, text: props.label, children: elementAndError })) : (elementAndError);
};
exports.Textarea = Textarea;
